import React, { CSSProperties, useEffect, useState } from 'react'
import { Col, Container, Dropdown, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { logout, selectAccountId, selectIsSystemGhost, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { useGetMeQuery } from '../../store/user/user'
import { useZocksIntercom } from '../../components/Intercom/hooks/useZocksIntercom'
import { getLoginAppBaseUrl, handleCompanySsoLogout } from '../../common/companySso'
import { formatDate, getUserDisplayName } from '../../apps/professional-app/fa/common/aifeatures/utils'
import { AccountInfoCard } from './AccountInfoCard'
import { useGetAccountQuery } from '../../store/accountUserSettings/accountUserSettings'
import './ProfileDropdown.scss'
import { Button } from '../../components/Button/Button'
import { DropdownProvider, useDropdown } from './DropdownContext'
import { SuccessToast } from '../../components/SuccessToast/SuccessToast'
import {
  CrossAccountGrantOfUser,
  CrossAccountGrantStatus,
  useAcceptGrantOfCurrentUserByIdMutation,
  useCancelGrantOfCurrentUserByIdMutation,
  useGetHomeAccountQuery,
  useListGrantsOfCurrentUserQuery,
  useRejectGrantOfCurrentUserByIdMutation,
} from '../../store/crossAccount/crossAccount'
import { useAccountSwitch } from './useAccountSwitch'

export interface ProfileDropdownProps {}

const CustomToggle = React.forwardRef<
  HTMLDivElement,
  {
    onClick: (e: React.MouseEvent<HTMLDivElement>) => void
    children: React.ReactNode
  }
>(({ children, onClick }, ref) => (
  <div
    role='button'
    ref={ref}
    onClick={(e) => {
      e.preventDefault()
      onClick(e)
    }}
  >
    {children}
  </div>
))

const generateUserId = (accountId: string, userId: string) => {
  return `${accountId}.${userId}`
}

const CustomMenu = React.forwardRef<
  HTMLDivElement,
  {
    children?: React.ReactNode
    style?: CSSProperties
    className?: string
    'aria-labelledby'?: string
  }
>(({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
  const navigate = useNavigate()
  const [value] = useState('')
  const accountId = useAppSelector(selectAccountId)
  const { data: currentUser } = useGetMeQuery({ accountId: accountId })
  const dispatch = useAppDispatch()
  const { hardShutdown } = useZocksIntercom()
  const { closeDropdown } = useDropdown()
  const [showToast, setShowToast] = useState(false)

  const onLogoutClick = () => {
    handleCompanySsoLogout()
    dispatch(logout({}))
    hardShutdown()
    window.location.href = getLoginAppBaseUrl()
  }
  const onProfileSettingsClick = () => {
    closeDropdown()
    navigate('/account/profile-settings/profile-settings')
  }

  const onCopyUserIdClick = async () => {
    if (currentUser) {
      await navigator.clipboard.writeText(generateUserId(accountId, currentUser.id))
    }
    closeDropdown()
    setShowToast(true)
  }

  return (
    <div
      ref={ref}
      style={style}
      className={`${className ? className : ''} zocks-p-0.75 zocks__custom-menu zocks__custom-menu__text_color`}
      aria-labelledby={labeledBy}
      dir='ltr'
    >
      <SuccessToast position={'bottom-center'} message='User ID copied to the clipboard.' show={showToast} onClose={() => setShowToast(false)} />
      <Row className='border zocks-border-0.5 p-3 m-0 pt-2 zocks-border-color-gray-200'>
        <Col xs='auto' className='justify-content-center align-items-center px-0'>
          <Row>
            <Col>
              <div className='paragraph-large fw-bold zocks-text-gray-900 text-center'>{currentUser ? getUserDisplayName(currentUser) : null}</div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className='paragraph-small zocks-text-gray-600 text-center'>{currentUser?.email}</div>
            </Col>
          </Row>
          <Row className='zocks-mt-1.25'>
            <Col className='d-flex align-items-start align-self-center justify-content-center gap-1'>
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip>
                    Copy and share the User ID to get
                    <br />
                    access to other accounts
                  </Tooltip>
                }
              >
                <Button className='flat-button' onClick={onCopyUserIdClick}>
                  <i className='bi bi-copy'></i>
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement='top' overlay={<Tooltip>Profile Settings</Tooltip>}>
                <Button className='flat-button' onClick={onProfileSettingsClick}>
                  <i className='bi bi-gear'></i>
                </Button>
              </OverlayTrigger>
              <OverlayTrigger placement='top' overlay={<Tooltip>Log out</Tooltip>}>
                <Button className='flat-button' onClick={onLogoutClick}>
                  <i className='bi bi-box-arrow-right'></i>
                </Button>
              </OverlayTrigger>
            </Col>
          </Row>
        </Col>
      </Row>
      <ul className='list-unstyled d-flex flex-column gap-1 m-0'>
        {React.Children.toArray(children).filter((child: any) => !value || child.props.children.toLowerCase().startsWith(value))}
      </ul>
    </div>
  )
})

interface CurrentUserCardProps {
  isExpanded: boolean
}

const CurrentUserCard: React.FC<CurrentUserCardProps> = ({ isExpanded }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: currentAccount } = useGetAccountQuery({ accountId })
  const { displayName } = useAppSelector(selectSafeAuthenticatedUser)
  return (
    <div className='border-start zocks-border-color-gray-200'>
      <AccountInfoCard accountName={currentAccount ? currentAccount.data.name : ''} text={displayName}>
        {isExpanded ? <i className='ps-3 pe-2 bi bi-chevron-contract' /> : <i className='ps-3 pe-2 bi bi-chevron-expand' />}
      </AccountInfoCard>
    </div>
  )
}

interface PendingGrantCardProps {
  grant: CrossAccountGrantOfUser
}

const PendingGrantCard: React.FC<PendingGrantCardProps> = ({ grant }) => {
  const [triggerAccept, { isLoading: isAcceptLoading }] = useAcceptGrantOfCurrentUserByIdMutation()
  const [triggerReject, { isLoading: isRejectLoading }] = useRejectGrantOfCurrentUserByIdMutation()

  const onAccept = () => {
    triggerAccept({ grantId: grant.id })
  }

  const onReject = () => {
    triggerReject({ grantId: grant.id })
  }

  return (
    <div className='grant-card border rounded-3'>
      <AccountInfoCard
        accountName={grant.targetAccountName}
        text={formatDate(grant.createdAt)}
        badgeColor={'var(--zocks-gray-200)'}
        badgeTextColor={'var(--zocks-gray-900)'}
      >
        {isAcceptLoading || isRejectLoading ? (
          <Row className='p-2'>
            <div className='skeleton' style={{ width: '100%', height: '16px' }} />
          </Row>
        ) : (
          <Row className='d-flex gap-1 p-2'>
            <Button className='icon-button icon-button--danger' onClick={onReject}>
              <i className='bi bi-x'></i>
            </Button>
            <Button className='icon-button icon-button--success' onClick={onAccept}>
              <i className='bi bi-check'></i>
            </Button>
          </Row>
        )}
      </AccountInfoCard>
    </div>
  )
}

interface ActiveGrantCardProps {
  grant: CrossAccountGrantOfUser
}

const ActiveGrantCard: React.FC<ActiveGrantCardProps> = ({ grant }) => {
  const { switchToAccount, currentAccountId } = useAccountSwitch()
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [triggerCancel] = useCancelGrantOfCurrentUserByIdMutation()

  const handleAccountSwitch = async () => {
    if (currentAccountId != grant.targetAccountId) {
      await switchToAccount(grant.targetAccountId)
    }
  }

  const onDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteConfirmation(!showDeleteConfirmation)
  }

  const onCancelClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDeleteConfirmation(false)
  }

  const onLeaveClick = () => {
    triggerCancel({ grantId: grant.id })
  }

  return (
    <>
      <div
        className={`grant-card border rounded-3 ${currentAccountId === grant.targetAccountId ? 'zocks-border-color-gray-900' : 'zocks-border-color-gray-200'}`}
      >
        <AccountInfoCard accountName={grant.targetAccountName} badgeColor={'var(--zocks-primary)'} onClick={handleAccountSwitch}>
          {currentAccountId != grant.targetAccountId ? (
            <Row className='p-2'>
              <Button className='icon-button' onClick={onDeleteClick}>
                <i className='bi bi-trash'></i>
              </Button>
            </Row>
          ) : null}
        </AccountInfoCard>
      </div>
      {showDeleteConfirmation && (
        <Container fluid className='delete-confirmation-card zocks-bg-color-danger-background rounded-3 mt-2 p-0 m-0 w-100 mw-100 overflow-hidden'>
          <Row className='p-3 align-items-center flex-nowrap justify-content-between mw-0'>
            <Col xs='auto' className='text-nowrap overflow-hidden text-truncate mw-0'>
              <span className='paragraph-small'>Leave {grant.targetAccountName}?</span>
            </Col>
            <Col xs='auto' className='flex-shrink-0'>
              <Row className='g-1'>
                <Col xs='auto'>
                  <Button className='button-small small-button' variant='grayDarkGhostButton' onClick={onCancelClick}>
                    Cancel
                  </Button>
                </Col>
                <Col xs='auto'>
                  <Button className='button-small small-button' variant='danger' onClick={onLeaveClick}>
                    Leave
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      )}
    </>
  )
}

const HomeAccountCard: React.FC = () => {
  const { switchToAccount, currentAccountId } = useAccountSwitch()
  const isSystemGhost = useAppSelector(selectIsSystemGhost)
  const { data: homeAccount } = useGetHomeAccountQuery(undefined, { skip: isSystemGhost })
  const handleHomeAccountSwitch = async () => {
    if (homeAccount && currentAccountId != homeAccount.id) {
      await switchToAccount(homeAccount.id)
    }
  }
  if (isSystemGhost) {
    return null
  }
  return (
    <div className={`border rounded-3 ${homeAccount && currentAccountId === homeAccount.id ? 'zocks-border-color-gray-900' : 'zocks-border-color-gray-200'}`}>
      {homeAccount ? (
        <AccountInfoCard accountName={homeAccount.name} text='Default' onClick={handleHomeAccountSwitch} />
      ) : (
        <Spinner className='text-center' size='sm' />
      )}
    </div>
  )
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = () => {
  const [isExpanded, setIsExpanded] = useState(false)
  const { data: activeAndPendingGrants, isLoading } = useListGrantsOfCurrentUserQuery(
    { statuses: [CrossAccountGrantStatus.PENDING, CrossAccountGrantStatus.ACTIVE] },
    { refetchOnMountOrArgChange: true },
  )
  const [activeGrants, setActiveGrants] = useState<CrossAccountGrantOfUser[]>([])
  const [pendingGrants, setPendingGrants] = useState<CrossAccountGrantOfUser[]>([])

  const closeDropdown = () => {
    setIsExpanded(false)
  }

  useEffect(() => {
    if (activeAndPendingGrants) {
      setActiveGrants(activeAndPendingGrants.data.filter((grant) => grant.status === CrossAccountGrantStatus.ACTIVE))
      setPendingGrants(activeAndPendingGrants.data.filter((grant) => grant.status === CrossAccountGrantStatus.PENDING))
    }
  }, [activeAndPendingGrants])

  return (
    <Col xs='auto' className='p-0'>
      <DropdownProvider onClose={closeDropdown}>
        <Dropdown show={isExpanded} onToggle={setIsExpanded}>
          <Dropdown.Toggle as={CustomToggle} id='dropdown-custom-components'>
            <CurrentUserCard isExpanded={isExpanded} />
          </Dropdown.Toggle>
          <Dropdown.Menu as={CustomMenu}>
            {!isLoading ? (
              <>
                {pendingGrants.length > 0 && (
                  <>
                    <Dropdown.ItemText className='ms-2 mt-2'>
                      <Row className='paragraph-small fw-bold'>Pending access ({pendingGrants.length})</Row>
                    </Dropdown.ItemText>
                    {pendingGrants.map((grant) => (
                      <Dropdown.Item className='p-0'>
                        <PendingGrantCard key={grant.id} grant={grant} />
                      </Dropdown.Item>
                    ))}
                  </>
                )}
                {activeGrants.length > 0 && (
                  <>
                    <Dropdown.ItemText className='ms-2 mt-2'>
                      <Row className='paragraph-small fw-bold'>External accounts ({activeGrants.length})</Row>
                    </Dropdown.ItemText>
                    <Dropdown.Item className='p-0'>
                      <HomeAccountCard />
                    </Dropdown.Item>
                    {activeGrants.map((grant) => (
                      <Dropdown.Item className='p-0'>
                        <ActiveGrantCard key={grant.id} grant={grant} />
                      </Dropdown.Item>
                    ))}
                  </>
                )}
              </>
            ) : (
              <Dropdown.ItemText className='text-center'>
                <Spinner size='sm' />
              </Dropdown.ItemText>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </DropdownProvider>
    </Col>
  )
}
